// =============================================================================
// audit/drapeauAudit.js : les drapeaux de menu de l'audit interne ET du
// contrôle interne (D1, D2), seul morceau du bundle principal.
// -----------------------------------------------------------------------------
// useDrapeauAudit(sessionId, dansConformite) →
//   { habilite, gererAcces, modeles, ci: { habilite, gererAcces, referentiel } }
// Deux modules, deux drapeaux (décision de Pierre du 14/10/2026 : l'audit
// s'ouvre par l'habilitation « AI », le contrôle interne par « CI », le DG
// de droit) : chacun est demandé à SON serveur (action etat), jamais déduit
// de l'autre.
//   • demandés dès l'ouverture d'Aramis, après le premier rendu ; la couche
//     d'accès arrive par import(), jamais dans le bundle principal ;
//   • mémorisés pour l'onglet (sessionStorage, par identifiant de session),
//     pour que le rail ne clignote pas ; mais un drapeau, positif comme
//     négatif, est REDEMANDÉ au rechargement, à l'entrée dans Conformité et
//     au retour sur l'onglet, au plus une fois par minute : un retrait d'habilitation se
//     voit vite (avant le 14/10/2026, un « oui » tenait tout l'onglet) ;
//   • échec fermé : sans réponse, le module reste hors du rail (ses vues
//     restent de toute façon gardées côté serveur) ;
//   • remis à jour par les écrans de chaque module quand ils relisent l'état
//     (noterDrapeauAudit, noterDrapeauCI).
// Le serveur sert l'identité du JETON : on passe la session RÉELLE, jamais
// celle de « Voir comme ».
// =============================================================================
import { useEffect, useMemo, useState } from "react";

const CI_VIDE = Object.freeze({ habilite: false, gererAcces: false, referentiel: false });
export const DRAPEAU_AUDIT_VIDE = Object.freeze({ habilite: false, gererAcces: false, modeles: false, ci: CI_VIDE });
const MODULES = Object.freeze({
  audit: { cle: "audit_drapeau", evenement: "aramis:drapeau-audit", champs: ["habilite", "gererAcces", "modeles"], acces: () => import("./acces") },
  ci: { cle: "ci_drapeau", evenement: "aramis:drapeau-ci", champs: ["habilite", "gererAcces", "referentiel"], acces: () => import("../controleInterne/acces") },
});
const DELAI_MS = 2500;
const RELANCE_MS = 60000;

/** Ne garde que les champs attendus (une mémoire altérée n'ouvre rien de plus). */
function propre(module, etat) {
  const h = !!(etat && etat.habilite === true);
  const d = (etat && etat.droits) || {};
  const out = {};
  for (const c of MODULES[module].champs) out[c] = c === "habilite" ? h : h && d[c] === true;
  return out;
}
export const drapeauAuditPropre = (etat) => propre("audit", etat);
export const drapeauCIPropre = (etat) => propre("ci", etat);
const vide = (module) => (module === "ci" ? CI_VIDE : { habilite: false, gererAcces: false, modeles: false });

function lireMemoire(module, sessionId) {
  if (!sessionId) return null;
  try {
    const m = JSON.parse(sessionStorage.getItem(MODULES[module].cle) || "null");
    if (!m || m.sessionId !== sessionId || !m.drapeau) return null;
    // Un droit sans « habilite » n'ouvre rien (mémoire altérée).
    const h = m.drapeau.habilite === true;
    const drapeau = {};
    for (const c of MODULES[module].champs) drapeau[c] = c === "habilite" ? h : h && m.drapeau[c] === true;
    return { drapeau, le: Number(m.le) || 0 };
  } catch { return null; }
}

function noter(module, sessionId, etat) {
  if (!sessionId) return;
  const drapeau = propre(module, etat);
  try { sessionStorage.setItem(MODULES[module].cle, JSON.stringify({ sessionId, drapeau, le: Date.now() })); } catch { /* onglet sans stockage */ }
  try { window.dispatchEvent(new CustomEvent(MODULES[module].evenement, { detail: { sessionId, drapeau } })); } catch { /* rien */ }
}
/** Retient l'état lu par un écran des audits et prévient le rail. */
export const noterDrapeauAudit = (sessionId, etat) => noter("audit", sessionId, etat);
/** Retient l'état lu par un écran du contrôle interne et prévient le rail. */
export const noterDrapeauCI = (sessionId, etat) => noter("ci", sessionId, etat);

function useDrapeau(module, sessionId, dansConformite) {
  const [drapeau, setDrapeau] = useState(() => (lireMemoire(module, sessionId) || {}).drapeau || vide(module));
  const [retour, setRetour] = useState(0);
  useEffect(() => {
    const surNote = (e) => { if (e && e.detail && e.detail.sessionId === sessionId) setDrapeau(e.detail.drapeau); };
    window.addEventListener(MODULES[module].evenement, surNote);
    // Au retour sur l'onglet, le drapeau se revérifie (au plus une fois par minute).
    const surVisible = () => { if (document.visibilityState === "visible") setRetour((n) => n + 1); };
    document.addEventListener("visibilitychange", surVisible);
    return () => { window.removeEventListener(MODULES[module].evenement, surNote); document.removeEventListener("visibilitychange", surVisible); };
  }, [module, sessionId]);
  useEffect(() => {
    if (!sessionId) { setDrapeau(vide(module)); return undefined; }
    const memoire = lireMemoire(module, sessionId);
    if (memoire) {
      setDrapeau(memoire.drapeau);
      // Revérifié à l'ouverture d'Aramis, à l'entrée dans Conformité et au
      // retour sur l'onglet, au plus une fois par minute.
      if (Date.now() - memoire.le < RELANCE_MS) return undefined;
    } else {
      setDrapeau(vide(module));
    }
    let vivant = true;
    const t = setTimeout(() => {
      MODULES[module].acces()
        .then((acces) => acces.appeler("etat"))
        .then((etat) => { if (vivant) noter(module, sessionId, etat); })
        .catch(() => {});
    }, memoire ? 0 : DELAI_MS);
    return () => { vivant = false; clearTimeout(t); };
  }, [module, sessionId, dansConformite, retour]);
  return drapeau;
}

export function useDrapeauAudit(sessionId, dansConformite) {
  const audit = useDrapeau("audit", sessionId, dansConformite);
  const ci = useDrapeau("ci", sessionId, dansConformite);
  return useMemo(() => ({ ...audit, ci }), [audit, ci]);
}
